import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import { faArrowRight as fasArrowRight } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { Button, FormCurrency } from "bm-ui-kit";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";

export interface StepFields {
	netIncome: number;
	operatingProfit: number;
	depreciation: number;
	equity: number;
}

export class Step5 extends AbstractStep<StepFields> {
	private readonly maxFtValue = 9_999_999_999;
	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		return (
			<>
				<Card.Body>
					<FormCurrency
						label="Utolsó lezárt év bevétele:"
						name="netIncome"
						validate={this.validNetIncome}
						required
					/>
					<FormCurrency
						label="Üzemi eredmény:"
						name="operatingProfit"
						infoTooltip="Működési profit, vagy üzemi eredmény (nem az adózott profit)"
						validate={this.validOperatingProfit}
						required
					/>
					<FormCurrency
						label="Értékcsökkenés:"
						name="depreciation"
						validate={this.validDepreciation}
						required
					/>
					<FormCurrency label="Saját tőke:" name="equity" validate={this.validEquity} required />
				</Card.Body>
				<Card.Footer className="text-center">
					<Button
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
					>
						Tovább
					</Button>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validNetIncome(netIncome: number, values: Record<string, any>): string {
		if (isNaN(netIncome)) {
			return "Add meg céged utolsó lezárt üzleti évi árbevételét!";
		}

		if (netIncome < 10_000_000) {
			return "A cég alacsony árbevétele miatt javasoljuk lakossági hitel felvételét.";
		}

		if (netIncome < values.operatingProfit) {
			return "Ellenőrizd, hogy jó számokat írtál-e be, üzemi eredményed nem lehet magasabb az árbevételednél!";
		}

		if (netIncome > this.maxFtValue) {
			return `Az árbevétel megadható maximális értéke ${formatText(this.maxFtValue, "ft")}`;
		}
	}

	@autobind
	private validOperatingProfit(operatingProfit: number, values: Record<string, any>): string {
		if (isNaN(operatingProfit)) {
			return "Add meg a céged utolsó lezárt üzleti év üzemi eredményét!";
		}

		if (operatingProfit < 0) {
			return "Sajnáljuk, de céged üzleti eredménye miatt jelenleg nem hitelképes.";
		}

		if (operatingProfit > values.netIncome) {
			return "Ellenőrizd, hogy jó számokat írtál-e be, üzemi eredményed nem lehet magasabb az árbevételednél!";
		}

		if (operatingProfit > this.maxFtValue) {
			return `Az üzemi eredmény megadható maximális értéke ${formatText(this.maxFtValue, "ft")}`;
		}
	}

	@autobind
	private validEquity(equity: number): string {
		if (equity < 0) {
			return "Negatív saját tőke esetén céged nem hitelezhető, hitelfelvételre a tőke rendezését követően lesz lehetősége cégednek.";
		}

		if (equity > this.maxFtValue) {
			return `Az saját tőke megadható maximális értéke ${formatText(this.maxFtValue, "ft")}`;
		}
	}

	@autobind
	private validDepreciation(depreciation: number): string {
		if (depreciation < 0) {
			return "Az értékcsökkenésed nem lehet negatív szám!";
		}

		if (depreciation > this.maxFtValue) {
			return `Az értékcsökkenés megadható maximális értéke ${formatText(this.maxFtValue, "ft")}`;
		}
	}

	protected getComponentName(): string {
		return "Step5";
	}
}
